import { useMemo } from 'react';

/**
 * Aggregates RecipeDisplay-local selection state derivations.
 *
 * The normalized panel data now comes from RecipeDisplayContext; this hook only
 * resolves the UI-selection state that is local to the RecipeDisplay component
 * (the timeline-selected run and the forced correction opened from a modal/TopNav).
 *
 * @param {Object} params
 * @param {Array<Object>} params.productionRuns - Current live production runs.
 * @param {Array<Object>} params.productionEvents - Current live production event documents.
 * @param {Object|null} params.selectedProductionRun - Run selected from the timeline.
 * @param {{correctionId?: string}|null} params.forcedCorrectionContext - Forced correction document ID.
 * @returns {{
 *   forcedCorrection: Object|null,
 *   enrichedSelectedProductionRunLive: Object|null
 * }}
 */
export function useRecipeDisplayEnrichment({
  productionRuns,
  productionEvents = [],
  selectedProductionRun,
  forcedCorrectionContext,
}) {
  const selectedProductionRunLive = useMemo(() => {
    if (!selectedProductionRun?.id) return null;
    return productionRuns.find((run) => run.id === selectedProductionRun.id) || selectedProductionRun;
  }, [productionRuns, selectedProductionRun]);

  const forcedCorrection = useMemo(() => {
    if (!forcedCorrectionContext?.correctionId) return null;
    return productionEvents.find((e) => e.id === forcedCorrectionContext.correctionId) || null;
  }, [forcedCorrectionContext?.correctionId, productionEvents]);

  const enrichedSelectedProductionRunLive = selectedProductionRunLive;

  return {
    forcedCorrection,
    enrichedSelectedProductionRunLive,
  };
}
